import { Box, Tag, TagLabel, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useGetQuestionnaireRounds, useGetQuestionsOfRound } from "../../../hooks/query";
import { constructQueryParamsString } from "../../../utils";

const STAGE_OPTIONS = [
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "INTERNAL_REVIEW", label: "Internal Review" },
  { value: "AUDIT", label: "Audit" },
  { value: "COMPLETE", label: "Complete" },
];

/**
 * "Reference ESG Actions data" selector for AI Review.
 * Shows foundation question groups as pill tags and a status filter.
 *
 * @param {string[]} selectedQuestionIds - Currently selected foundation question IDs
 * @param {function} setSelectedQuestionIds - Setter for selected IDs
 */
export default function FoundationSourceSelector({ selectedQuestionIds, setSelectedQuestionIds }) {
  const paramsString = constructQueryParamsString({ page: 0, size: 500 });
  const { data: roundsData } = useGetQuestionnaireRounds(paramsString);
  const foundationRound = roundsData?.content?.find((r) => r.foundationRound);

  const { data: questions } = useGetQuestionsOfRound(null, foundationRound?.id, {
    enabled: !!foundationRound?.id,
  });

  const [selectedStages, setSelectedStages] = useState(() => STAGE_OPTIONS.map((s) => s.value));
  const [selectedGroupNames, setSelectedGroupNames] = useState(new Set());

  const groupedQuestions = useMemo(() => {
    if (!questions) return {};
    return questions.reduce((acc, q) => {
      const key = q.questionGroup || "Ungrouped";
      if (!acc[key]) acc[key] = [];
      acc[key].push(q);
      return acc;
    }, {});
  }, [questions]);

  const presentStages = useMemo(() => {
    if (!questions) return new Set();
    return new Set(questions.map((q) => q.stageType));
  }, [questions]);

  // Default all groups to ON when data first loads
  const initRef = useRef(false);
  useEffect(() => {
    const groupNames = Object.keys(groupedQuestions);
    if (groupNames.length === 0 || initRef.current) return;
    initRef.current = true;
    setSelectedGroupNames(new Set(groupNames));
  }, [groupedQuestions]);

  // Sync selectedQuestionIds whenever groups or stages change
  useEffect(() => {
    if (!questions || Object.keys(groupedQuestions).length === 0) return;
    const newIds = [];
    Object.entries(groupedQuestions).forEach(([groupName, groupQs]) => {
      if (selectedGroupNames.has(groupName)) {
        groupQs.forEach((q) => {
          if (selectedStages.includes(q.stageType)) {
            newIds.push(q.id);
          }
        });
      }
    });
    setSelectedQuestionIds(newIds);
  }, [selectedStages, selectedGroupNames, groupedQuestions, questions, setSelectedQuestionIds]);

  const handleToggleStage = (stageValue) => {
    setSelectedStages((prev) =>
      prev.includes(stageValue) ? prev.filter((s) => s !== stageValue) : [...prev, stageValue]
    );
  };

  const handleToggleGroup = (groupName) => {
    setSelectedGroupNames((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  if (!foundationRound) return null;

  const groups = Object.entries(groupedQuestions);
  if (groups.length === 0) return null;

  return (
    <Box>
      <Wrap spacing={2} mb={4}>
        {groups.map(([groupName, groupQs]) => {
          const hasEligible = groupQs.some((q) => selectedStages.includes(q.stageType));
          const isSelected = selectedGroupNames.has(groupName);
          return (
            <WrapItem key={groupName}>
              <Tag
                size="sm"
                borderRadius="full"
                bg={isSelected && hasEligible ? "lightLime" : "gray.100"}
                border="1px solid"
                borderColor={isSelected && hasEligible ? "lime" : "gray.300"}
                color={isSelected && hasEligible ? "inherit" : "gray.400"}
                px={3}
                py={1}
                cursor={hasEligible ? "pointer" : "not-allowed"}
                opacity={hasEligible ? 1 : 0.5}
                onClick={() => hasEligible && handleToggleGroup(groupName)}
              >
                <TagLabel>{groupName}</TagLabel>
                <Box as="span" ml={1} fontSize="xs">
                  {isSelected && hasEligible ? "✕" : "+"}
                </Box>
              </Tag>
            </WrapItem>
          );
        })}
      </Wrap>

      <Box>
        <Text fontSize="sm" fontWeight="600" color="gray.600" mb={2}>
          In status
        </Text>
        <Wrap spacing={2}>
          {STAGE_OPTIONS.map((stage) => {
            const hasQuestions = presentStages.has(stage.value);
            const isSelected = selectedStages.includes(stage.value);
            return (
              <WrapItem key={stage.value}>
                <Tag
                  size="sm"
                  borderRadius="full"
                  bg={isSelected && hasQuestions ? "lightLime" : "gray.100"}
                  border="1px solid"
                  borderColor={isSelected && hasQuestions ? "lime" : "gray.300"}
                  color={isSelected && hasQuestions ? "inherit" : "gray.400"}
                  px={3}
                  py={1}
                  cursor={hasQuestions ? "pointer" : "not-allowed"}
                  opacity={hasQuestions ? 1 : 0.5}
                  onClick={() => hasQuestions && handleToggleStage(stage.value)}
                >
                  <TagLabel>{stage.label}</TagLabel>
                  <Box as="span" ml={1} fontSize="xs">
                    {isSelected && hasQuestions ? "✕" : "+"}
                  </Box>
                </Tag>
              </WrapItem>
            );
          })}
        </Wrap>
      </Box>
    </Box>
  );
}
